import { useLocation } from 'react-router-dom'
import {
  Column,
  ColumnDef,
  ColumnSizingInfoState,
  ColumnSizingState,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  RowPinningState,
  RowSelectionState,
  SortingState,
  Table,
  TableMeta,
  Updater,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'
import { compact, entries, isEmpty, isEqual } from 'lodash'
import { useEffect, useMemo, useRef, useState } from 'react'
import { SELECTION_COLUMN_ID, selectionColumn } from './bulkActions/selectionColumn'
import type { BulkActionsConfig } from './bulkActions/types'
import { FilterableColumn, FilterObject } from 'app/filter.utils'
import { useSettings } from 'app/hooks/useSettings'
import {
  INITIAL_COLUMN_SIZING_INFO,
  adjustLastColumnWidth,
  calculateColumnWidths,
  getColumnWidthsToSave,
} from 'app/lib/calculateColumnWidths'
import { bg } from 'lib/background'
import { debug } from 'lib/logging'

function isColumnSizingState(value: unknown): value is ColumnSizingState {
  return !!value && typeof value === `object` && !Array.isArray(value)
}

export type UseSimpleTableProps<T> = {
  data?: T[]
  columns: ColumnDef<T, any>[]
  filterableColumns?: FilterableColumn[]
  showColumnOrdering?: boolean
  className?: string
  onSearchValueChange?: (searchValue: string) => void
  onScroll?: React.UIEventHandler<HTMLDivElement>
  wrapperClassName?: string
  extraSlot?: React.ReactElement
  extraRightSlot?: React.ReactElement
  hideFooter?: boolean
  enableExport?: boolean
  onExport?: () => Promise<void>
  onSortChange?: (sort: SortingState) => void
  isManuallySorted?: boolean
  onFiltersChange?: (filters: string) => void
  initialFilters?: FilterObject[]
  initialSort?: SortingState
  syncedSort?: SortingState
  noDataMessage?: string
  /** Rich replacement for the plain no-data text (e.g. an <EmptyState /> card). */
  emptyState?: React.ReactElement
  onColumnSizingChange?: (sizing: Record<string, number>) => void
  syncedColumnSizing?: Record<string, number>
  onColumnVisibilityChange?: (columnVisibility: { [k: string]: boolean }) => void
  syncedColumnVisibility?: { [k: string]: boolean }
  syncedColumnOrder?: string[]
  onColumnOrderChange?: (columnOrder: string[]) => void
  activeSubpage?: string
  meta?: TableMeta<T>
  enableHoverStateStyling?: boolean
  enableDeepLinking?: boolean
  edgePadded?: boolean
  /**
   * Turns on bulk selection: a leading checkbox column, a "Bulk actions"
   * switch in the toolbar, and the docked bar that applies this config. Absent
   * (the usual case) the table has no selection state at all.
   */
  bulkActions?: BulkActionsConfig<T>
}

export type UseSimpleTableReturn<T> = {
  table: Table<T>
  tableWrapperRef: React.RefObject<HTMLDivElement | null>
  data: T[] | undefined
  columnVisibility: { [k: string]: boolean }
  defaultColumnVisibility: { [k: string]: boolean }
  defaultColumnOrder: string[]
  toggleSort: (column: Column<T, unknown>) => void
  sortOrder: { columnId?: string; order?: `asc` | `desc` } | undefined
  onResetSizing: () => void
  onAutoFitColumn: (columnId: string, width: number) => void
  /** True while the checkboxes are showing and the docked bar is up. */
  selectionMode: boolean
  toggleSelectionMode: () => void
  exitSelectionMode: () => void
  // Pass-through props for the presentation layer
  props: UseSimpleTableProps<T>
}

export function useSimpleTable<T>(props: UseSimpleTableProps<T>): UseSimpleTableReturn<T> {
  const {
    data,
    columns: inputColumns,
    onSortChange,
    isManuallySorted,
    onColumnSizingChange,
    syncedColumnSizing,
    syncedColumnVisibility,
    onColumnVisibilityChange,
    syncedColumnOrder,
    onColumnOrderChange,
    syncedSort,
    activeSubpage,
    meta,
    initialSort,
    bulkActions,
  } = props

  const location = useLocation()
  const [selectionMode, setSelectionMode] = useState(false)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  // Anchor row (id) + last shift-key state, for shift-click range selection.
  const selectionAnchorRef = useRef<string | null>(null)
  const shiftKeyRef = useRef(false)

  // The checkbox column joins the column set permanently, so column order and
  // saved widths stay put; only its visibility follows selection mode.
  const columns = useMemo(
    () => (bulkActions ? [selectionColumn<T>(selectionAnchorRef, shiftKeyRef), ...inputColumns] : inputColumns),
    [inputColumns, bulkActions],
  )
  const [columnVisibility, setColumnVisibility] = useState<{ [k: string]: boolean }>(() => {
    if (syncedColumnVisibility && !isEmpty(syncedColumnVisibility)) {
      return syncedColumnVisibility
    }
    return Object.fromEntries(columns?.map((column) => [column.id as string, column.meta?.[`defaultVisible`] ?? false]))
  })
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
  const columnSizingRef = useRef<ColumnSizingState>(columnSizing)
  const validColumnIds = useMemo(() => new Set(columns?.map((col) => col.id as string) ?? []), [columns])
  const getDefaultColumnOrder = () => {
    if (syncedColumnOrder && syncedColumnOrder.length) {
      return syncedColumnOrder.filter((id) => validColumnIds.has(id))
    }
    return columns?.map((column) => column.id as string) ?? []
  }
  const [columnOrder, setColumnOrder] = useState<string[]>(() => getDefaultColumnOrder())
  const defaultColumnVisibility = Object.fromEntries(
    columns?.map((column) => [column.id as string, column.meta?.[`defaultVisible`] ?? false]),
  )
  const defaultColumnOrder = columns?.map((column) => column.id as string) ?? []
  const [sorting, setSorting] = useState<SortingState>(initialSort ?? [])
  const [rowPinning, setRowPinning] = useState<RowPinningState>({ top: [], bottom: [] })
  const [expanded, setExpanded] = useState<ExpandedState>({})
  const [tableData, setTableData] = useState<T[]>(data ?? [])
  const tableWrapperRef = useRef<HTMLDivElement>(null)
  const resizingColumnRef = useRef<string | false>(false)
  const lastColManualWidthRef = useRef<number | null>(null)
  const [columnSizingInfo, setColumnSizingInfo] = useState<ColumnSizingInfoState>(INITIAL_COLUMN_SIZING_INFO)
  const columnSizingSetting = useMemo(
    () => `columnSizing-${activeSubpage ? `${location.pathname}${activeSubpage}` : location.pathname}`,
    [activeSubpage],
  )
  const [settings, setSettings, settingsLoaded, settingsReadFailed] = useSettings(columnSizingSetting)
  const settingsRef = useRef(settings)
  const [userChangedSizing, setUserChangedSizing] = useState(false)
  const [sizingInitialized, setSizingInitialized] = useState(false)

  useEffect(() => {
    columnSizingRef.current = columnSizing
  }, [columnSizing])

  useEffect(() => {
    settingsRef.current = settings
  }, [settings])

  // TanStack Table exposes unstable callbacks by design; the React compiler cannot memoize this API safely.
  // oxlint-disable-next-line react/incompatible-library
  const table = useReactTable({
    data: tableData,
    columns,
    columnResizeMode: `onChange`,
    onColumnSizingInfoChange: (updater) => {
      setColumnSizingInfo((prev) => {
        const newInfo = typeof updater === `function` ? updater(prev) : updater
        resizingColumnRef.current = newInfo.isResizingColumn
        return newInfo
      })
    },
    state: {
      columnVisibility: bulkActions ? { ...columnVisibility, [SELECTION_COLUMN_ID]: selectionMode } : columnVisibility,
      columnSizing,
      columnSizingInfo,
      columnOrder,
      sorting,
      rowPinning,
      expanded,
      ...(bulkActions && { rowSelection }),
    },
    initialState: { columnVisibility, columnOrder },
    ...(meta && { meta }),
    ...(bulkActions && {
      enableRowSelection: true,
      onRowSelectionChange: setRowSelection,
      // Key selection by the row's stable id, never the array index: another
      // page arriving would otherwise re-map an existing selection onto
      // whichever rows happen to land in those slots.
      getRowId: (row: any) => String(row?.id),
    }),
    defaultColumn: {
      size: 250,
      minSize: 60,
    },
    onExpandedChange: setExpanded,
    // @ts-ignore
    getSubRows: (row) => row.subRows,
    getExpandedRowModel: getExpandedRowModel(),
    onColumnVisibilityChange: (_columnVisibility: Updater<VisibilityState>) => {
      if (typeof _columnVisibility === `function`) {
        _columnVisibility = _columnVisibility(columnVisibility)
      }
      if (onColumnVisibilityChange) {
        onColumnVisibilityChange(_columnVisibility)
      }
      setColumnVisibility(_columnVisibility)
      const newColumnEntries = entries(_columnVisibility).filter(([, visible]) => visible === true)
      const oldColumnEntries = entries(columnVisibility).filter(([, visible]) => visible === true)
      if (newColumnEntries.length > oldColumnEntries.length) {
        let newColumns = newColumnEntries.filter((col) => !oldColumnEntries.find((oldEntry) => oldEntry[0] === col[0]))
        let newColumnWidth = 250
        let newColumnWidths = newColumns.reduce((acc, col) => ({ ...acc, [col[0]]: newColumnWidth }), {})
        if (
          tableWrapperRef.current?.clientWidth &&
          entries(table.getState().columnSizing).reduce((acc, [k, v]) => acc + v, 0) +
            newColumns.length * newColumnWidth >
            tableWrapperRef.current?.clientWidth &&
          entries(table.getState().columnSizing).reduce((acc, [k, v]) => acc + v, 0) <=
            tableWrapperRef.current?.clientWidth + 100
        ) {
          let columns = table.getState().columnOrder
          let columnSizing = table.getState().columnSizing
          let visibleColumns = columns.filter((id) => table.getColumn(id)?.getIsVisible())
          let lastColumn = visibleColumns[visibleColumns.length - 1]
          columnSizing[lastColumn] = newColumnWidth
          setColumnSizing({ ...columnSizing, ...newColumnWidths })
        }
      }
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: (_sorting: Updater<SortingState>) => {
      if (typeof _sorting === `function`) {
        _sorting = _sorting(sorting)
      }
      if (onSortChange && !isEqual(syncedSort, _sorting)) {
        onSortChange(_sorting)
      }
      setSorting(_sorting)
    },
    getSortedRowModel: !isManuallySorted ? getSortedRowModel() : undefined,
    onColumnSizingChange: (_columnSizing: Updater<ColumnSizingState>) => {
      if (typeof _columnSizing === `function`) {
        _columnSizing = _columnSizing(columnSizing)
      }
      const containerWidth = tableWrapperRef.current?.clientWidth
      if (containerWidth) {
        lastColManualWidthRef.current = adjustLastColumnWidth(
          table,
          _columnSizing,
          columnSizing,
          containerWidth,
          // 3 + 6 for the DataTable scroll container's `pr-1.5` right padding,
          // so the last column fills to the visible area and doesn't force a
          // horizontal scrollbar.
          9,
          resizingColumnRef.current,
          lastColManualWidthRef.current,
        )
      }
      setColumnSizing(_columnSizing)
      if (userChangedSizing) {
        onColumnSizingChange?.(_columnSizing)
      }
    },
    onColumnOrderChange: (_columnOrder: Updater<string[]>) => {
      if (typeof _columnOrder === `function`) {
        _columnOrder = _columnOrder(columnOrder)
      }
      setColumnOrder(_columnOrder)
      if (onColumnOrderChange) {
        onColumnOrderChange(_columnOrder)
      }
    },
    onRowPinningChange: setRowPinning,
    keepPinnedRows: true,
    sortingFns: {
      onlySortParentRows: (rowA, rowB, columnId) => {
        if (rowA.depth === 0 && rowB.depth === 0) {
          return (rowA.getValue(columnId) as string) > (rowB.getValue(columnId) as string)
            ? 1
            : (rowA.getValue(columnId) as string) < (rowB.getValue(columnId) as string)
              ? -1
              : 0
        }
        return 0
      },
    },
  })

  useEffect(() => {
    if (columnSizingInfo.isResizingColumn && !userChangedSizing) {
      setUserChangedSizing(true)
    }
  }, [columnSizingInfo.isResizingColumn])

  useEffect(() => {
    if (settingsLoaded && isEmpty(settings)) setColumnOrder(getDefaultColumnOrder())
  }, [columns])

  useEffect(() => {
    if (
      syncedColumnVisibility &&
      !isEmpty(syncedColumnVisibility) &&
      !isEqual(columnVisibility, syncedColumnVisibility)
    ) {
      setColumnVisibility(syncedColumnVisibility)
    }
  }, [syncedColumnVisibility])

  useEffect(() => {
    if (syncedColumnOrder && syncedColumnOrder.length) {
      const filteredOrder = syncedColumnOrder.filter((id) => validColumnIds.has(id))
      if (!isEqual(columnOrder, filteredOrder)) {
        setColumnOrder(filteredOrder)
      }
    }
  }, [syncedColumnOrder, validColumnIds])

  useEffect(() => {
    if (syncedColumnSizing && !isEmpty(syncedColumnSizing) && !isEqual(columnSizing, syncedColumnSizing)) {
      setColumnSizing(syncedColumnSizing)
    }
  }, [syncedColumnSizing])

  useEffect(() => {
    if (sizingInitialized) return
    if (syncedColumnSizing && !isEmpty(syncedColumnSizing)) {
      setSizingInitialized(true)
      return
    }
    if (!settingsLoaded) return

    const containerWidth = tableWrapperRef.current?.clientWidth
    if (!containerWidth) {
      const timer = setTimeout(() => setSizingInitialized(false), 100)
      return () => clearTimeout(timer)
    }

    const savedSizing = isColumnSizingState(settings) ? settings : {}
    const { sizing, sizingToSave, lastColumnSavedWidth } = calculateColumnWidths(table, containerWidth, savedSizing, 60)
    debug(`columns`, `SimpleTable initializing`, { containerWidth, settings, sizing, sizingToSave })

    if (lastColumnSavedWidth !== null) {
      lastColManualWidthRef.current = lastColumnSavedWidth
    }
    if (!isEmpty(sizing)) {
      setColumnSizing(sizing)
      onColumnSizingChange?.(sizing)
      // A failed read looks like nothing saved; saving defaults then would overwrite the user's widths.
      if (isEmpty(savedSizing) && !isEmpty(sizingToSave) && !settingsReadFailed) {
        bg(async () => setSettings(columnSizingSetting, sizingToSave))
      }
    }
    setSizingInitialized(true)
  }, [sizingInitialized, settings, settingsLoaded, syncedColumnSizing])

  useEffect(() => {
    if (!sizingInitialized) return
    if (syncedColumnSizing && !isEmpty(syncedColumnSizing)) return
    if (resizingColumnRef.current !== false) return

    const containerWidth = tableWrapperRef.current?.clientWidth
    if (!containerWidth) return

    const currentSettings = isColumnSizingState(settingsRef.current) ? settingsRef.current : {}
    const { sizing, sizingToSave } = calculateColumnWidths(table, containerWidth, currentSettings, 60)
    if (!isEmpty(sizing) && !isEqual(sizing, columnSizing)) {
      setColumnSizing(sizing)
      onColumnSizingChange?.(sizing)
      if (!isEmpty(sizingToSave) && !isEqual(sizingToSave, currentSettings) && !settingsReadFailed) {
        bg(async () => setSettings(columnSizingSetting, sizingToSave))
      }
    }
  }, [columnVisibility])

  useEffect(() => {
    if (!sizingInitialized || !tableWrapperRef.current) return
    if (syncedColumnSizing && !isEmpty(syncedColumnSizing)) return

    const observer = new ResizeObserver((entries) => {
      const containerWidth = entries[0]?.contentRect.width
      if (!containerWidth) return
      if (resizingColumnRef.current !== false) return

      const currentSettings = isColumnSizingState(settingsRef.current) ? settingsRef.current : {}
      const { sizing } = calculateColumnWidths(table, containerWidth, currentSettings, 60)
      if (!isEmpty(sizing) && !isEqual(sizing, columnSizingRef.current)) {
        setColumnSizing(sizing)
        onColumnSizingChange?.(sizing)
      }
    })

    observer.observe(tableWrapperRef.current)
    return () => observer.disconnect()
  }, [sizingInitialized, syncedColumnSizing])

  useEffect(() => {
    if (!userChangedSizing) return
    if (columnSizingInfo.isResizingColumn !== false) return
    if (isEmpty(columnSizing)) return

    const sizingToSave = getColumnWidthsToSave(table, columnSizing, lastColManualWidthRef.current !== null)
    if (!isEmpty(sizingToSave) && !isEqual(settings, sizingToSave)) {
      bg(async () => setSettings(columnSizingSetting, sizingToSave))
    }
  }, [columnSizingInfo, columnSizing, userChangedSizing])

  const toggleSort = (column: Column<T, unknown>) => {
    const currentSort = sorting.find((s) => s.id === column.id)
    let newSorting: SortingState

    if (!currentSort) {
      newSorting = [{ id: column.id, desc: false }]
    } else if (!currentSort.desc) {
      newSorting = [{ id: column.id, desc: true }]
    } else {
      newSorting = []
    }

    if (onSortChange && !isEqual(syncedSort, newSorting)) {
      onSortChange(newSorting)
    }
    setSorting(newSorting)
  }

  useEffect(() => {
    if (syncedSort && !isEqual(sorting, syncedSort)) {
      setSorting(syncedSort)
    }
  }, [syncedSort])

  useEffect(() => {
    debug(`table`, `data change`, data)
    if (data) {
      setTableData(data)
    } else {
      setTableData([])
    }
  }, [data])

  const onResetSizing = () => {
    setColumnSizing({})
    bg(async () => setSettings(columnSizingSetting, {}))
  }

  // Double-click-to-fit lands here. Flagging it as a user sizing change is what lets
  // the save effect above persist the new width, exactly as a finished drag does.
  const onAutoFitColumn = (columnId: string, width: number) => {
    setUserChangedSizing(true)
    table.setColumnSizing((prev) => ({ ...prev, [columnId]: width }))
  }

  // Leaving selection mode drops the selection with it: coming back to a bar
  // still holding rows nobody can see the checkboxes for is how a bulk action
  // gets applied to a set somebody has forgotten they picked.
  const exitSelectionMode = () => {
    setSelectionMode(false)
    setRowSelection({})
  }

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => {
      if (prev) setRowSelection({})
      return !prev
    })
  }

  return {
    table,
    selectionMode,
    toggleSelectionMode,
    exitSelectionMode,
    tableWrapperRef,
    data,
    columnVisibility,
    defaultColumnVisibility,
    defaultColumnOrder,
    toggleSort,
    sortOrder: syncedSort?.length
      ? { columnId: syncedSort[0].id, order: syncedSort[0].desc ? `desc` : `asc` }
      : undefined,
    onResetSizing,
    onAutoFitColumn,
    props,
  }
}
